import React, { useMemo, useState } from "react";
import Layout from "../../components/layout/layout";
import Faq from "../../components/storyblok/faq";
import { parseStory, responsiveGet } from "../../components/utils";
import slugify from "slugify";
import ExtraHelpForm from "../../components/extra-help-form";
import PageHeader from "../../components/page-header";
import assistenzaAnim from "../../resources/images/headers/assistenza.json";
import useWindowSize from "../../components/use-window-size";

const cat2titles = {
  acquisti: "Domande sugli acquisti",
  prodotti: "Domande sui prodotti",
  servizi: "Domande sui servizi",
};

const blocchiFaq = ["acquisti", "prodotti", "servizi"];

const generateFaqObject = (faqNodes) =>
  faqNodes.reduce((faqObject, faq) => {
    // faq node is already parsed
    const [cat] = faq["tag_list"]
      .filter((t) => t.startsWith("c-"))
      .map((t) => t.slice(2));
    const [subcat] = faq["tag_list"]
      .filter((t) => t.startsWith("s-"))
      .map((t) => t.slice(2));

    if (cat && subcat) {
      if (cat in faqObject) {
        faqObject[cat][subcat] = faqObject[cat][subcat] || [];
        faqObject[cat][subcat].push(faq);
      } else {
        faqObject[cat] = { [subcat]: [faq] };
      }
    }
    return faqObject;
  }, {});

const processNode = (node) => {
  if (node.type === "text") {
    return [slugify(node.text)];
  } else if ("content" in node) {
    return node.content.flatMap(processNode);
  } else {
    return [];
  }
};

const filterFaq = (query) => (faqNode) => {
  const tokens = slugify(query).toLowerCase().split("-");
  const singleMatchMaxScore = tokens.length * 2;
  const t = faqNode.slug;
  const q = slugify(faqNode.content.question).toLowerCase();
  const a = processNode(faqNode.content.answer).join("-").toLowerCase();
  const tags = faqNode.tag_list
    .filter((t) => t[0] === "c" || t[0] === "s")
    .map((t) => t.slice(2))
    .join("-");
  const score = tokens.reduce((cum, tok) => {
    const re = new RegExp(tok, "g");
    let s = (t.match(re) ?? []).length;
    s += (q.match(re) ?? []).length;
    s += (a.match(re) ?? []).length;
    s += (tags.match(re) ?? []).length;
    return cum + s;
  }, 0);
  console.log({ t, q, a, tags, score });
  return score >= singleMatchMaxScore / 2;
};

const FaqPage = ({ data }) => {
  const [parsedFaqNodes] = useState(
    data.allStoryblokEntry.nodes.map(parseStory)
  );
  const [organizedFaq, setOrganizedFaq] = useState(
    generateFaqObject(parsedFaqNodes)
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [timeout, setT] = useState(null);
  const [numResults, setNumResults] = useState(-1);

  const subcat2titles = useMemo(() => {
    try {
      const s = parseStory(data.storyblokEntry);
      return s.content.subcats.reduce((s2t, subcat) => {
        s2t[subcat.slug] = {
          tit:
            subcat?.name ??
            subcat.slug.charAt(0).toUpperCase() + subcat.slug.slice(1),
          sub: subcat?.text ?? "Domande",
        };
        return s2t;
      }, {});
    } catch (e) {
      console.log(e);
      return {};
    }
  }, [data.storyblokEntry]);

  const { width } = useWindowSize();

  return (
    <Layout>
      <PageHeader
        bg="bg-ci-beige"
        img="green"
        tit="Assistenza Clienti"
        sub="Come possiamo aiutarti oggi?"
        anim={assistenzaAnim}
        posInfo={responsiveGet(width, {
          def: { right: "-30px", top: "10px" },
          md: { top: "50px", right: "-410px" },
          lg: { top: "35px", right: "-210px" },
        })}
      />
      <div className="ci-cont">
        <div className="relative">
          <input
            type="text"
            className="block border rounded-full w-full mt-14 pl-10 pr-52 py-8 text-base outline-none focus:ring-black focus:ring-1 border-ci-gray-light"
            placeholder="Cerca il tuo problema in modo che possiamo trovare la soluzione giusta per te."
            value={searchQuery}
            onChange={(e) => {
              const query = e.target.value;
              setSearchQuery(query);
              if (timeout !== null) {
                clearTimeout(timeout);
              }
              setT(
                setTimeout(() => {
                  if (query === "") {
                    setOrganizedFaq(generateFaqObject(parsedFaqNodes));
                    setNumResults(-1);
                  } else {
                    const filt = filterFaq(query);
                    const filteredFaqs = parsedFaqNodes.filter(filt);
                    setOrganizedFaq(generateFaqObject(filteredFaqs));
                    setNumResults(filteredFaqs.length);
                  }
                }, 300)
              );
            }}
          />
          <button className="h-full absolute top-0 right-0 w-44 bg-ci-red text-white uppercase text-xl rounded-full font-bold tracking-wide">
            Cerca
          </button>
        </div>
        {numResults >= 0 && (
          <p className="ci-sottotitolo mt-4 pl-10">
            {numResults} risultati trovati
          </p>
        )}
        {blocchiFaq.map(
          (name) =>
            name in organizedFaq && (
              <FaqCatBlock
                key={name}
                cat={name}
                faqs={organizedFaq[name]}
                isProdotti={name === "prodotti"}
                s2t={subcat2titles}
              />
            )
        )}
        <ExtraHelpForm />
      </div>
    </Layout>
  );
};

const FaqCatBlock = ({ cat, faqs, isProdotti, s2t }) => {
  const [b1, b2] = useMemo(() => {
    if (isProdotti) {
      return Object.entries(faqs).reduce(
        ([b1, b2], [sc, fs]) => {
          if (["baby", "family", "together", "unique"].includes(sc)) {
            b1[sc] = fs;
          } else {
            b2[sc] = fs;
          }
          return [b1, b2];
        },
        [{}, {}]
      );
    } else {
      const targetQ =
        Object.values(faqs).reduce((cum, fs) => cum + (fs?.length ?? 0), 0) / 2;
      let cur = 0;
      return Object.entries(faqs).reduce(
        ([b1, b2], [sc, fs]) => {
          if (cur < targetQ * 0.9) {
            b1[sc] = fs;
            cur += fs.length;
          } else {
            b2[sc] = fs;
          }
          return [b1, b2];
        },
        [{}, {}]
      );
    }
  }, [faqs, isProdotti]);

  return (
    <div className="mt-20" id={cat}>
      <p className="text-center ci-sottotitolo">Faq</p>
      <h2
        className={
          "ci-h2 text-center mt-3.5 pb-7 border-ci-gray-light border-b " +
          (cat === "acquisti" ? "smoothed" : "")
        }
      >
        {cat2titles[cat]}
      </h2>
      <div className="flex gap-8">
        <div className="flex-1">
          {Object.entries(b1).map(([sc, fs]) => (
            <FaqSubcatBlock subcat={sc} key={sc} faqs={fs} s2t={s2t} />
          ))}
        </div>
        <div className="flex-1">
          {Object.entries(b2).map(([sc, fs]) => (
            <FaqSubcatBlock subcat={sc} key={sc} faqs={fs} s2t={s2t} />
          ))}
        </div>
      </div>
    </div>
  );
};

const FaqSubcatBlock = ({ subcat, faqs, s2t }) => (
  <div className="mt-14">
    <div id={subcat}>
      <h3 className="ci-h2">
        {s2t[subcat]?.tit ?? subcat.charAt(0).toUpperCase() + subcat.slice(1)}
      </h3>
      <p className="ci-sottotitolo pb-7 border-ci-gray-light border-b">
        {s2t[subcat]?.sub ?? "Domande"}
      </p>
    </div>
    {faqs.map((faq) => (
      <Faq faq={faq} key={faq.name} />
    ))}
  </div>
);

export default FaqPage;
